import { useTranslation } from '../../../shared/i18n';
import React, { useState, useEffect } from 'react';
import { Card, Row, Col, Button, Tag, Space, message, Divider, Spin, Empty, Select, InputNumber, Tooltip } from 'antd';
import { ShoppingCartOutlined, SafetyOutlined, CheckCircleOutlined } from '@ant-design/icons';
import sslService from '../../services/sslService';
import { cartService } from '../../services/cartService';
import { useNavigate } from 'react-router-dom';

import { userPath } from '../../../shared/config/paths';
import { formatMoney } from '../../../shared/utils/money';

const Browse = () => {
  const { t } = useTranslation();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedBrand, setSelectedBrand] = useState('all');
  const [yearsById, setYearsById] = useState({});
  const [acmeById, setAcmeById] = useState({});
  const navigate = useNavigate();

  useEffect(() => {
    fetchProducts();
  }, []);

  const fetchProducts = async () => {
    setLoading(true);
    try {
      const response = await sslService.getProducts('ssl'); // 只列网站证书：代码签名有自己的页面（/ssl/codesigning）
      if (response.success) {
        setProducts(response.data || []);
      }
    } catch (error) {
      message.error(t('Failed to load product'));
    } finally {
      setLoading(false);
    }
  };

  // ACME 免费证书一次性；固定年限（≥2 年）按目录卖；其余付费证书可选 1-3 年
  const isAcme = (product) => ['letsencrypt', 'acme', 'zerossl'].includes(String(product.provider || '').toLowerCase());
  const canPickYears = (product) => !product.caas && !isAcme(product) && (product.validity_period_days || 365) < 700;
  const yearsOf = (product) => (canPickYears(product) ? (yearsById[product.id] || 1) : 1);

  // ACME CaaS: base + single / wildcard domains per year (at least 1 in total)
  const acmeOf = (product) => acmeById[product.id] || { single: product.acme_prices?.single != null ? 1 : 0, wildcard: 0 };
  const acmeTotal = (product) => {
    const p = product.acme_prices || {};
    const q = acmeOf(product);
    return Math.round(((p.base || 0) + q.single * (p.single || 0) + q.wildcard * (p.wildcard || 0)) * 100) / 100;
  };
  const acmeValid = (product) => {
    const q = acmeOf(product);
    return product.acme_prices && q.single + q.wildcard >= 1;
  };

  const addSsl = (product) => {
    if (product.caas) {
      const q = acmeOf(product);
      return cartService.addItem({ product_id: product.id, billing_cycle: 'annually', quantity: 1, acme_single: q.single || 0, acme_wildcard: q.wildcard || 0 });
    }
    const item = { product_id: product.id, billing_cycle: 'onetime', quantity: 1 };
    if (!isAcme(product)) item.years = yearsOf(product);
    return cartService.addItem(item);
  };

  const handleAddToCart = async (product) => {
    try {
      const r = await addSsl(product);
      if (r && r.success === false) throw r;
      window.dispatchEvent(new CustomEvent('cart:updated'));
      message.success({
        content: (
          <span>
            {t('Added to cart')}
            <a style={{ marginLeft: 12 }} onClick={() => navigate(userPath('/billing/cart'))}>{t('View cart')}</a>
          </span>
        ),
        duration: 5,
      });
    } catch (error) {
      message.error(t('Failed to add: {{error}}', { error: error.error || error.message || t('Unknown error') }));
    }
  };

  const handleBuyNow = async (product) => {
    try {
      const r = await addSsl(product);
      if (r && r.success === false) throw r;
      window.dispatchEvent(new CustomEvent('cart:updated'));
      navigate(userPath('/billing/cart'));
    } catch (error) {
      message.error(t('Operation failed: {{error}}', { error: error.error || error.message || t('Unknown error') }));
    }
  };

  // Backend `type` may be a short code (DV/OV/EV) or a descriptive metadata
  // string (e.g. "Domain Validation", "Organization Validation"). Normalize
  // to a canonical key so colors/features map correctly.
  const normalizeType = (type) => {
    if (!type) return '';
    const t = String(type).toLowerCase();
    if (t.includes('wildcard')) return 'Wildcard';
    if (t.includes('multi') || t.includes('san')) return 'Multi-Domain';
    if (t === 'dv' || t.includes('domain')) return 'DV';
    if (t === 'ov' || t.includes('organization') || t.includes('organisation')) return 'OV';
    if (t === 'ev' || t.includes('extended')) return 'EV';
    return type;
  };

  const getTypeColor = (type) => {
    const colors = {
      DV: 'green',
      OV: 'blue',
      EV: 'gold',
      Wildcard: 'purple',
      'Multi-Domain': 'cyan',
    };
    return colors[normalizeType(type)] || 'default';
  };

  const getTypeFeatures = (type, product) => {
    if (product?.caas) return [t('Your own ACME account + EAB'), t('Auto-renew with certbot / acme.sh'), t('Priced per domain')];
    const features = {
      DV: [t('Domain verification'), t('Issued in 10 minutes'), t('For personal use/testing')],
      OV: [t('Organization validation'), t('Issued in 1-3 days'), t('For business websites')],
      EV: [t('Extended validation'), t('Green address bar'), t('Highest trust level')],
      Wildcard: [t('Supports wildcards'), t('Unlimited subdomains'), t('Cost savings')],
      'Multi-Domain': [t('Multiple domain support'), t('SAN certificate'), t('Up to 250 domains')],
    };
    return features[normalizeType(type)] || [];
  };

  // 按证书品牌分组（不是上游渠道）：同一品牌不同写法归一，缺品牌的按渠道名
  const BRAND_NAMES = { digicert: 'DigiCert', sectigo: 'Sectigo', geotrust: 'GeoTrust', thawte: 'Thawte', rapidssl: 'RapidSSL', globalsign: 'GlobalSign', certum: 'Certum', zerossl: 'ZeroSSL', "let's encrypt": "Let's Encrypt", letsencrypt: "Let's Encrypt" };
  const brandOf = (p) => {
    const raw = String(p.brand || p.provider || '').trim();
    return BRAND_NAMES[raw.toLowerCase()] || raw;
  };
  const brands = [...new Set(products.map(brandOf).filter(Boolean))];

  const filteredProducts = selectedBrand === 'all'
    ? products
    : products.filter(p => brandOf(p) === selectedBrand);

  return (
    <div>
      <Card>
        <div style={{ marginBottom: '24px' }}>
          <h2><SafetyOutlined /> {t('SSL certificate store')}</h2>
          <p style={{ color: '#666' }}>{t('Choose an SSL certificate to secure your website')}</p>
        </div>

        <Space style={{ marginBottom: '24px' }} wrap>
          <Button
            type={selectedBrand === 'all' ? 'primary' : 'default'}
            onClick={() => setSelectedBrand('all')}
          >
            {t('All')}
          </Button>
          {brands.map(brand => (
            <Button
              key={brand}
              type={selectedBrand === brand ? 'primary' : 'default'}
              onClick={() => setSelectedBrand(brand)}
            >
              {brand}
            </Button>
          ))}
        </Space>

        <Spin spinning={loading}>
        <Row gutter={[16, 16]}>
          {filteredProducts.map(product => (
            <Col xs={24} sm={12} lg={8} key={product.id}>
              <Card
                hoverable
                style={{ height: '100%', display: 'flex', flexDirection: 'column' }}
                bodyStyle={{ flex: 1, display: 'flex', flexDirection: 'column' }}
              >
                <div style={{ flex: 1 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '12px' }}>
                    <div>
                      <h3 style={{ margin: 0 }}>{product.name}</h3>
                      <div style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
                        {brandOf(product)}
                      </div>
                    </div>
                    <Tag color={product.caas ? 'geekblue' : getTypeColor(product.type)}>{product.caas ? 'ACME' : normalizeType(product.type)}</Tag>
                  </div>

                  <Divider style={{ margin: '12px 0' }} />

                  <div style={{ marginBottom: '12px' }}>
                    {getTypeFeatures(product.type, product).map((feature, idx) => (
                      <div key={idx} style={{ marginBottom: '6px' }}>
                        <CheckCircleOutlined style={{ color: '#52c41a', marginRight: '8px' }} />
                        <span style={{ fontSize: '14px' }}>{feature}</span>
                      </div>
                    ))}
                  </div>

                  {product.caas ? (
                  <div style={{ marginTop: 'auto' }}>
                    {['single', 'wildcard'].filter((k) => product.acme_prices?.[k] != null).map((k) => (
                      <div key={k} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
                        <Tooltip title={k === 'wildcard' ? '*.example.com' : 'example.com'}>
                          <span>{k === 'wildcard' ? t('Wildcard domains') : t('Standard domains')}</span>
                        </Tooltip>
                        <Space size={6}>
                          <span style={{ fontSize: 12, color: '#999' }}>{formatMoney(product.acme_prices[k], product.currency)}</span>
                          <InputNumber size="small" min={0} max={255} value={acmeOf(product)[k]} style={{ width: 72 }}
                            onChange={(v) => setAcmeById((m) => ({ ...m, [product.id]: { ...acmeOf(product), [k]: v || 0 } }))} />
                        </Space>
                      </div>
                    ))}
                    <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#1890ff', marginTop: 8 }}>
                      {formatMoney(acmeTotal(product), product.currency)}
                      <span style={{ fontSize: '14px', fontWeight: 'normal' }}>{t('/year')}</span>
                    </div>
                  </div>
                  ) : (
                  <div style={{ marginTop: 'auto' }}>
                    <div style={{ fontSize: '12px', color: '#666' }}>
                      {t('Validity: {{days}} days', { days: product.validity_period_days })}
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: '8px' }}>
                      <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#1890ff' }}>
                        {Number(product.sale_price || 0) === 0
                          ? t('Free')
                          : formatMoney(Math.round(Number(product.sale_price) * yearsOf(product) * 100) / 100, product.currency)}
                        {Number(product.sale_price || 0) > 0 && canPickYears(product) && yearsOf(product) === 1 && (
                          <span style={{ fontSize: '14px', fontWeight: 'normal' }}>{t('/year')}</span>
                        )}
                      </div>
                      {canPickYears(product) && (
                        <Select
                          size="small"
                          value={yearsOf(product)}
                          onChange={(v) => setYearsById((m) => ({ ...m, [product.id]: v }))}
                          options={[1, 2, 3].map((y) => ({ value: y, label: t('{{n}} yr', { n: y }) }))}
                          style={{ width: 80 }}
                        />
                      )}
                    </div>
                  </div>
                  )}
                </div>

                <Divider style={{ margin: '12px 0' }} />

                <Space style={{ width: '100%', justifyContent: 'space-between' }}>
                  <Button
                    icon={<ShoppingCartOutlined />}
                    disabled={product.caas && !acmeValid(product)}
                    onClick={() => handleAddToCart(product)}
                  >
                    {t('Add to cart')}
                  </Button>
                  <Button
                    type="primary"
                    disabled={product.caas && !acmeValid(product)}
                    onClick={() => handleBuyNow(product)}
                  >
                    {t('Buy now')}
                  </Button>
                </Space>
              </Card>
            </Col>
          ))}
        </Row>

        {filteredProducts.length === 0 && !loading && (
          <Empty
            style={{ padding: '48px' }}
            description={t('No products available')}
          />
        )}
        </Spin>
      </Card>
    </div>
  );
};

export default Browse;
